import React, { useEffect, useState, useRef } from 'react';
// Types
import { ContactType, TargetType } from 'api/graphql-global-types';
import { GetUnregisteredContact_getUnregisteredContact_entities } from 'api/mentions/types/GetUnregisteredContact';
// Components
import MillionsMentionsSelect from './components/MillionsMentionsSelect/MilionsMentionsSelect';
import OutsideMillionsAthletesSelect from './components/OutsideMillionsAthletesSelect/OutsideMillionsAthletesSelect';
import OutsideMillionsBrandsSelect from './components/OutsideMillionsBrandsSelect/OutsideMillionsBrandsSelect';
// Ui
import Text from 'ui3/Text/Text';
// Styles
import styles from './Mentions.module.scss';

export type MillionsMention = {
  label: string | null;
  value: string | null;
  avatarURL: string | null;
};

interface User {
  id: string;
  name: string;
  avatarUrl: string;
}

export type Mention = {
  id: string | null;
  targetType: TargetType | null;
  user?: User | null;
  unregisteredContact: GetUnregisteredContact_getUnregisteredContact_entities | null;
};

type MentionsProps = {
  mentions: Mention[];
  onChange: (value: Mention[]) => void;
};

export const Mentions = ({ mentions, onChange }: MentionsProps) => {
  const [millionsMentions, setMillionsMentions] = useState<Mention[]>([]);
  const [
    unregisteredAthletesMentions,
    setUnregisteredAthletesMentions,
  ] = useState<Mention[]>([]);
  const [unregisteredBrandsMention, setUnregisteredBrandsMention] = useState<
    Mention[]
  >([]);
  const prevMentionsRef = useRef<Mention[]>([]); // To track previous mentions value

  useEffect(() => {
    const registeredUsers =
      mentions?.filter(
        (mention) => mention.targetType === TargetType.REGISTERED
      ) || [];
    const unregisteredUsers =
      mentions?.filter(
        (mention) => mention.targetType === TargetType.UNREGISTERED
      ) || [];
    // Handle raw form data (new unregistered mentions without targetType)
    const rawUnregisteredMentions =
      mentions?.filter(
        (mention) =>
          !mention.targetType &&
          ((mention as unknown) as { type: ContactType }).type
      ) || [];

    setMillionsMentions(registeredUsers);

    const unregisteredAthletes = [
      ...(unregisteredUsers || []).filter(
        (mention) => mention?.unregisteredContact?.type === ContactType.ATHLETE
      ),
      ...(rawUnregisteredMentions || []).filter(
        (mention) =>
          ((mention as unknown) as { type: ContactType }).type ===
          ContactType.ATHLETE
      ),
    ];
    const unregisteredBrands = [
      ...(unregisteredUsers || []).filter(
        (mention) => mention?.unregisteredContact?.type === ContactType.BRAND
      ),
      ...(rawUnregisteredMentions || []).filter(
        (mention) =>
          ((mention as unknown) as { type: ContactType }).type ===
          ContactType.BRAND
      ),
    ];

    setUnregisteredAthletesMentions(unregisteredAthletes);
    setUnregisteredBrandsMention(unregisteredBrands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentions]);

  useEffect(() => {
    const currentMentions = [
      ...millionsMentions,
      ...unregisteredAthletesMentions,
      ...unregisteredBrandsMention,
    ];

    const prevMentions = prevMentionsRef.current;

    // Only trigger onChange if the new mentions differ from the previous mentions
    if (JSON.stringify(currentMentions) !== JSON.stringify(prevMentions)) {
      // Use setTimeout to defer the onChange call until after rendering
      setTimeout(() => {
        onChange(currentMentions);
        prevMentionsRef.current = currentMentions; // Update previous mentions to the latest value
      }, 0);
    }
  }, [
    millionsMentions,
    unregisteredAthletesMentions,
    unregisteredBrandsMention,
    onChange,
  ]);

  const handleMillionsMentionChange = (mentions: Mention[]) => {
    setMillionsMentions(mentions);
  };

  const handleUnregisteredAthleteMentionChange = (mentions: Mention[]) =>
    setUnregisteredAthletesMentions(mentions);

  const handleUnregisteredBrandsMentionChange = (mentions: Mention[]) =>
    setUnregisteredBrandsMention(mentions);

  const handleMillionsMentionRemove = (index: number) => {
    setMillionsMentions((prevMentions) => {
      const updatedMentions = [...prevMentions];
      updatedMentions.splice(index, 1);
      return updatedMentions;
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text variant="subtitle2" className={styles.title}>
          Mentions
        </Text>
        <Text
          variant="body2Regular"
          color="lights-low"
          className={styles.description}
        >
          Mention athletes, brands, and organizations by using their names and
          links to directly engage them and increase interaction on your
          content.
        </Text>
      </div>

      <div className={styles.millionsMentionsSection}>
        <MillionsMentionsSelect
          mentions={millionsMentions}
          onChange={handleMillionsMentionChange}
          handleMillionsMentionRemove={handleMillionsMentionRemove}
        />
      </div>

      <OutsideMillionsAthletesSelect
        mentions={unregisteredAthletesMentions}
        onChange={handleUnregisteredAthleteMentionChange}
      />

      <OutsideMillionsBrandsSelect
        mentions={unregisteredBrandsMention}
        onChange={handleUnregisteredBrandsMentionChange}
      />
    </div>
  );
};

export default Mentions;
